import React, { useState, useEffect } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { formatINRFull, getStatusColor, formatStatus, EXPENSE_CATEGORIES, PAYMENT_MODES, PAYMENT_STATUSES, formatDate } from '@/lib/utils';
import { Plus, Search, Trash2, Download, Upload, Paperclip, Edit2, X, Save } from 'lucide-react';
import FileUpload from '@/components/FileUpload';

export default function ExpensesPage() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filterProject, setFilterProject] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  const fetchExpenses = () => {
    const params = new URLSearchParams();
    if (filterProject) params.set('project_id', filterProject);
    if (filterCategory) params.set('category', filterCategory);
    api.get(`/expenses?${params}`).then(r => setExpenses(r.data.expenses || [])).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchExpenses(); }, [filterProject, filterCategory]);
  useEffect(() => { api.get('/projects').then(r => setProjects(r.data.projects || [])); }, []);

  const canCreate = ['DIRECTOR', 'ACCOUNTANT', 'HR_ADMIN'].includes(user?.role);
  const canEdit = ['DIRECTOR', 'ACCOUNTANT', 'HR_ADMIN'].includes(user?.role);
  const isViewOnly = ['SALES', 'SUPERVISOR'].includes(user?.role);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const startEdit = (expense) => {
    setEditingId(expense.id);
    setEditForm({ amount: expense.amount, description: expense.description, category: expense.category, vendor_name: expense.vendor_name || '', payment_mode: expense.payment_mode, date: expense.date });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/expenses/${editingId}`, editForm);
      setEditingId(null);
      fetchExpenses();
    } catch (err) { alert('Update failed'); }
  };

  const exportCSV = async () => {
    try {
      const params = filterProject ? `?project_id=${filterProject}` : '';
      const res = await api.get(`/utility/export/expenses${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'expenses_export.csv'; a.click();
    } catch (err) { alert('Export failed'); }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    await api.delete(`/expenses/${id}`);
    fetchExpenses();
  };

  const totalAmount = expenses.reduce((s, e) => s + (e.total_amount || 0), 0);
  const bankTotal = expenses.filter(e => ['BANK_TRANSFER', 'UPI', 'CHEQUE'].includes(e.payment_mode)).reduce((s, e) => s + (e.total_amount || 0), 0);
  const cashTotal = expenses.filter(e => e.payment_mode === 'CASH').reduce((s, e) => s + (e.total_amount || 0), 0);

  return (
    <div data-testid="expenses-page" className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight">Expenses</h1>
          <p className="text-sm text-slate-500">{expenses.length} entries | Total: {formatINRFull(totalAmount)} (Bank: {formatINRFull(bankTotal)} | Cash: {formatINRFull(cashTotal)})</p>
        </div>
        {canCreate && (
          <div className="flex gap-2">
            <button onClick={exportCSV} data-testid="export-expenses-csv"
              className="border border-slate-200 text-slate-600 px-3 py-2 rounded-md text-sm font-medium hover:bg-slate-50 flex items-center gap-2">
              <Download size={16} /> Export CSV
            </button>
            <button onClick={() => setShowCreate(true)} data-testid="add-expense-button"
              className="bg-[#1B5486] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#144066] flex items-center gap-2">
              <Plus size={16} /> Add Expense
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <select value={filterProject} onChange={e => setFilterProject(e.target.value)} data-testid="filter-expense-project"
          className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.project_code} - {p.name}</option>)}
        </select>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none">
          <option value="">All Categories</option>
          {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{formatStatus(c)}</option>)}
        </select>
      </div>

      {loading ? <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div> : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead><tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Date</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Project</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Category</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Description</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Vendor</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Amount</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">GST</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Total</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Payment</th>
                <th className="text-center py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Receipt</th>
                {canCreate && <th className="w-10"></th>}
              </tr></thead>
              <tbody>
                {expenses.map(e => (
                  <tr key={e.id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-2.5 px-4 text-slate-500">{editingId === e.id ? <input type="date" value={editForm.date} onChange={ev => setEditForm({...editForm, date: ev.target.value})} className="input-field text-xs w-28" /> : formatDate(e.date)}</td>
                    <td className="py-2.5 px-4 text-xs font-medium text-[#1B5486]">{e.project_code || '-'}</td>
                    <td className="py-2.5 px-4">{editingId === e.id ? <select value={editForm.category} onChange={ev => setEditForm({...editForm, category: ev.target.value})} className="input-field text-xs">{EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{formatStatus(c)}</option>)}</select> : <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{formatStatus(e.category)}</span>}</td>
                    <td className="py-2.5 px-4 text-slate-900 max-w-[200px] truncate">{editingId === e.id ? <input value={editForm.description} onChange={ev => setEditForm({...editForm, description: ev.target.value})} className="input-field text-xs w-full" /> : e.description}</td>
                    <td className="py-2.5 px-4 text-slate-500">{editingId === e.id ? <input value={editForm.vendor_name} onChange={ev => setEditForm({...editForm, vendor_name: ev.target.value})} className="input-field text-xs w-24" /> : (e.vendor_name || '-')}</td>
                    <td className="py-2.5 px-4 text-right font-mono">{editingId === e.id ? <input type="number" value={editForm.amount} onChange={ev => setEditForm({...editForm, amount: ev.target.value})} className="input-field text-xs w-20 text-right" /> : formatINRFull(e.amount)}</td>
                    <td className="py-2.5 px-4 text-right font-mono text-slate-500">{formatINRFull(e.gst_amount)}</td>
                    <td className="py-2.5 px-4 text-right font-mono font-medium">{formatINRFull(e.total_amount)}</td>
                    <td className="py-2.5 px-4"><span className={`text-xs px-1.5 py-0.5 rounded ${e.payment_mode === 'CASH' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{formatStatus(e.payment_mode)}</span></td>
                    <td className="py-2.5 px-4 text-center">{e.receipt_url ? <a href={`${process.env.REACT_APP_BACKEND_URL}${e.receipt_url}`} target="_blank" rel="noreferrer" className="text-[#1B5486] hover:underline"><Paperclip size={14} className="inline" /></a> : <span className="text-slate-300">-</span>}</td>
                    {canCreate && <td className="py-2.5 px-2">
                      {editingId === e.id ? (
                        <div className="flex gap-1">
                          <button onClick={saveEdit} data-testid={`save-expense-${e.id}`} className="text-emerald-600 hover:text-emerald-800"><Save size={14} /></button>
                          <button onClick={() => setEditingId(null)} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          <button onClick={() => startEdit(e)} data-testid={`edit-expense-${e.id}`} className="text-slate-300 hover:text-[#1B5486]"><Edit2 size={14} /></button>
                          <button onClick={() => deleteExpense(e.id)} data-testid={`delete-expense-${e.id}`} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>
                        </div>
                      )}
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {expenses.length === 0 && <div className="text-center py-12 text-slate-400">No expenses found</div>}
          </div>
        </div>
      )}

      {showCreate && <CreateExpenseModal projects={projects} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchExpenses(); }} />}
    </div>
  );
}

function CreateExpenseModal({ projects, onClose, onCreated }) {
  const { token } = useAuth();
  const [form, setForm] = useState({
    project_id: '', boq_item_id: '', date: new Date().toISOString().slice(0, 10), category: 'MATERIAL',
    description: '', vendor_name: '', quantity: '1', uom: 'Nos', unit_cost: '', amount: '',
    gst_percent: '18', gst_amount: '0',
    payment_mode: 'BANK_TRANSFER', payment_status: 'PAID', receipt_url: ''
  });
  const [boqItems, setBoqItems] = useState([]);
  const [saving, setSaving] = useState(false);

  // Load BOQ items when project selected
  useEffect(() => {
    if (form.project_id) {
      api.get(`/projects/${form.project_id}`).then(r => {
        setBoqItems(r.data.project?.boq_items || []);
      }).catch(() => setBoqItems([]));
    } else {
      setBoqItems([]);
    }
  }, [form.project_id]);

  const handleUnitCalc = (field, value) => {
    const updated = { ...form, [field]: value };
    const uc = parseFloat(field === 'unit_cost' ? value : updated.unit_cost) || 0;
    const qty = parseFloat(field === 'quantity' ? value : updated.quantity) || 0;
    if (uc > 0 && qty > 0) updated.amount = String(uc * qty);
    // Auto-calc GST
    const base = parseFloat(updated.amount) || 0;
    const gstPct = parseFloat(updated.gst_percent) || 0;
    if (base > 0 && gstPct > 0) updated.gst_amount = String(Math.round(base * gstPct / 100));
    setForm(updated);
  };

  const handleAmountChange = (value) => {
    const updated = { ...form, amount: value };
    const base = parseFloat(value) || 0;
    const gstPct = parseFloat(updated.gst_percent) || 0;
    if (base > 0 && gstPct > 0) updated.gst_amount = String(Math.round(base * gstPct / 100));
    setForm(updated);
  };

  const handleGstPctChange = (value) => {
    const updated = { ...form, gst_percent: value };
    const base = parseFloat(updated.amount) || 0;
    const gstPct = parseFloat(value) || 0;
    updated.gst_amount = String(Math.round(base * gstPct / 100));
    setForm(updated);
  };

  const handleBoqSelect = (boqId) => {
    const item = boqItems.find(b => b.id === boqId);
    if (item) {
      setForm({...form, boq_item_id: boqId, description: item.description || item.item_name || '', unit_cost: String(item.actual_unit_cost || ''), uom: item.unit || 'Nos'});
    } else {
      setForm({...form, boq_item_id: boqId});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post('/expenses', form); onCreated(); } catch (err) { alert(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="create-expense-modal">
        <h2 className="text-lg font-semibold text-slate-900 font-heading mb-4">Add Expense</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Project *</label>
            <select value={form.project_id} onChange={e => setForm({...form, project_id: e.target.value})} required className="input-field w-full" data-testid="expense-project-select">
              <option value="">Select project</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.project_code} - {p.name}</option>)}
            </select></div>
          {boqItems.length > 0 && (
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Link to BOQ Item (optional)</label>
              <select value={form.boq_item_id} onChange={e => handleBoqSelect(e.target.value)} className="input-field w-full" data-testid="expense-boq-item">
                <option value="">Not linked</option>
                {boqItems.map(b => <option key={b.id} value={b.id}>{b.sl_no}. {b.item_name || b.description} ({b.unit})</option>)}
              </select></div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
              <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="input-field w-full">
                {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{formatStatus(c)}</option>)}</select></div>
          </div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Description *</label>
            <input value={form.description} onChange={e => setForm({...form, description: e.target.value})} required className="input-field w-full" data-testid="expense-description" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Vendor</label>
            <input value={form.vendor_name} onChange={e => setForm({...form, vendor_name: e.target.value})} className="input-field w-full" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Quantity</label>
              <input type="number" value={form.quantity} onChange={e => handleUnitCalc('quantity', e.target.value)} className="input-field w-full" data-testid="expense-quantity" /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">UOM</label>
              <select value={form.uom} onChange={e => setForm({...form, uom: e.target.value})} className="input-field w-full" data-testid="expense-uom">
                {['Nos', 'Mtrs', 'Box', 'Set', 'LS', 'Lot', 'Kg', 'Sqm', 'Rmt', 'Litre', 'Trip', 'Day', 'Month'].map(u => <option key={u}>{u}</option>)}</select></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Unit Cost</label>
              <input type="number" value={form.unit_cost} onChange={e => handleUnitCalc('unit_cost', e.target.value)} className="input-field w-full" data-testid="expense-unit-cost" /></div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Base Amount *</label>
              <input type="number" value={form.amount} onChange={e => handleAmountChange(e.target.value)} required className="input-field w-full" data-testid="expense-amount" /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">GST %</label>
              <select value={form.gst_percent} onChange={e => handleGstPctChange(e.target.value)} className="input-field w-full" data-testid="expense-gst-percent">
                {['0', '5', '12', '18', '28'].map(g => <option key={g} value={g}>{g}%</option>)}</select></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">GST Amount</label>
              <input type="number" value={form.gst_amount} readOnly className="input-field w-full bg-slate-50 cursor-not-allowed" data-testid="expense-gst-amount" /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Total Amount</label>
              <div className="input-field w-full bg-emerald-50 text-emerald-700 font-semibold" data-testid="expense-total-display">
                {formatINRFull((parseFloat(form.amount) || 0) + (parseFloat(form.gst_amount) || 0))}
              </div></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Payment Mode</label>
              <select value={form.payment_mode} onChange={e => setForm({...form, payment_mode: e.target.value})} className="input-field w-full">
                {PAYMENT_MODES.map(m => <option key={m} value={m}>{formatStatus(m)}</option>)}</select></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Payment Status</label>
              <select value={form.payment_status} onChange={e => setForm({...form, payment_status: e.target.value})} className="input-field w-full">
                {PAYMENT_STATUSES.map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}</select></div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Receipt / Bill</label>
            <FileUpload token={token} label="Upload receipt (JPG, PNG, PDF)" onUploaded={(url) => setForm(prev => ({...prev, receipt_url: url}))} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-slate-200 py-2 rounded-md text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="submit-expense-btn" className="flex-1 bg-[#1B5486] text-white py-2 rounded-md text-sm font-medium hover:bg-[#144066] disabled:opacity-60">
              {saving ? 'Saving...' : 'Add Expense'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
